   import { Component, HostListener, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ConclaveApiService } from '../../conclave/conclave-api.service';

type FieldValidationErrors = Record<string, boolean>;
type FormErrorValue = string | FieldValidationErrors;

@Component({
  selector: 'app-conclave-create',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './conclave-create.component.html',
  styleUrl: './conclave-create.component.css'
})
export class ConclaveCreateComponent implements OnDestroy {
  private _coverObjectUrl: string | null = null;
  private readonly titlePattern = /^[A-Za-z0-9 .&-]+$/;
  private readonly descriptionPattern = /^[A-Za-z0-9 ,.:;!?()\/&-]+$/;
  private readonly venuePattern = /^[A-Za-z0-9 ,.\-/#]+$/;
  private readonly cityStatePattern = /^[A-Za-z ]+$/;

  // Form model (bound from template)
  title = '';
  description = '';
  venue = '';
  city = '';
  state = '';

  no_of_days: number | null = null;

  start_date = '';
  start_time = '';
  end_date = '';
  end_time = '';

  // Frontend validation state
  isSubmitted = false;
  formErrors: Record<string, FormErrorValue> = {};

  private toISODate(input: Date): string {

    // Convert to YYYY-MM-DD in local time for <input type="date"> compatibility
    const year = input.getFullYear();
    const month = String(input.getMonth() + 1).padStart(2, '0');
    const day = String(input.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private computeNoOfDays(): void {
    const start = this.parseDateInput(this.start_date);
    const end = this.parseDateInput(this.end_date);

    if (!start || !end || end.getTime() < start.getTime()) {
      this.no_of_days = null;
      return;
    }

    const millisecondsPerDay = 24 * 60 * 60 * 1000;
    this.no_of_days = Math.floor((end.getTime() - start.getTime()) / millisecondsPerDay) + 1;
  }

  onNoOfDaysChange() {
    this.computeNoOfDays();
  }

  onStartDateChange() {
    this.computeNoOfDays();
    this.onValidatedScheduleChange();
  }

  onEndDateChange() {
    this.computeNoOfDays();
    this.onValidatedScheduleChange();
  }

  onStartTimeChange() {
    this.onValidatedScheduleChange();
  }

  onEndTimeChange() {
    this.onValidatedScheduleChange();
  }

  onRegistrationLinkChange(): void {
    if (!this.isSubmitted) return;

    this.validateForm();
  }

  onRegistrationDeadlineChange(): void {
    if (!this.isSubmitted) return;

    this.validateForm();
  }

  onPricePerHeadInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const digits = input.value.replace(/\D/g, '').slice(0, 7);
    input.value = digits;
    this.price_per_head = digits ? Number(digits) : null;

    if (this.isSubmitted) {
      this.validatePricePerHead();
    }
  }


  ticket_type: 'Free' | 'Paid' = 'Free';
  price_per_head: number | null = null;

  participant_type: 'Anyone' | 'Group' = 'Anyone';
  selected_groups: number[] = [];
  groupsDropdownOpen = false;

  registerion_link = '';
  registration_deadline_date = '';

  // multipart upload
  selectedCoverFile: File | null = null;
  coverImagePreviewUrl: string | null = null;
  coverValidationError = '';

    // backend expects a STRING for cover_image_url, so we do NOT send File here.
    // (If you later add a separate upload endpoint, this can hold the returned URL.)
    cover_image_url = '';

    is_active = true;

  isSubmitting = false;
  errorMessage = '';

  isPublishLocked = false;


  // Loaded from: GET /group/get-all
  groups: Array<{ id: number; group_name: string; is_active?: boolean; student_count?: number }> = [];
  isLoadingGroups = false;

  ngOnInit() {
    this.loadGroups();
  }

  onCoverFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0] ?? null;

    // cleanup previous preview
    if (this._coverObjectUrl) {
      URL.revokeObjectURL(this._coverObjectUrl);
      this._coverObjectUrl = null;
    }

    this.selectedCoverFile = file;

    if (!file) {
      this.coverImagePreviewUrl = null;
      this.cover_image_url = '';
      return;
    }

    // Validate file type and size
    const allowedMimeTypes = ['image/jpeg', 'image/png'];
    const maxBytes = 2 * 1024 * 1024; // 2 MB

    if (!allowedMimeTypes.includes(file.type) || file.size > maxBytes) {
      // reject file
      this.selectedCoverFile = null;
      this.coverImagePreviewUrl = null;
      this.cover_image_url = '';

      if (!allowedMimeTypes.includes(file.type)) {
        this.coverValidationError = 'Cover page must be a JPG/JPEG/PNG image.';
      } else if (file.size > maxBytes) {
        this.coverValidationError = 'Cover page size must not be greater than 2 MB.';
      } else {
        this.coverValidationError = 'Invalid cover page file.';
      }

      return;
    }

    this.coverValidationError = '';

    // only preview locally; backend expects cover_image_url as a string
    this._coverObjectUrl = URL.createObjectURL(file);
    this.coverImagePreviewUrl = this._coverObjectUrl;
    this.cover_image_url = '';
  }


  private loadGroups() {
    this.isLoadingGroups = true;

    this.conclaveApi.getAllGroups().subscribe({
      next: (res: any) => {
        this.groups = res?.data ?? [];
        this.selected_groups = [];
        this.isLoadingGroups = false;
      },
      error: (err) => {
        console.error(err);
        this.groups = [];
        this.selected_groups = [];
        this.isLoadingGroups = false;
      }
    });
  }

  constructor(
    private readonly conclaveApi: ConclaveApiService,
    private readonly router: Router,
  ) {}

  ngOnDestroy() {
    if (this._coverObjectUrl) {
      URL.revokeObjectURL(this._coverObjectUrl);
      this._coverObjectUrl = null;
    }
  }

  toggleSelectedGroup(groupId: number) {
    const exists = this.selected_groups.includes(groupId);
    this.selected_groups = exists
      ? this.selected_groups.filter((id) => id !== groupId)
      : [...this.selected_groups, groupId];

    // Live validation for group selection (only when participant_type === 'Group')
    if (this.participant_type === 'Group') {
      if (this.selected_groups.length === 0) {
        this.formErrors = {
          ...this.formErrors,
          selected_groups: 'Please select at least one group.',
        };
      } else {
        const { selected_groups: _removed, ...remainingErrors } = this.formErrors;
        this.formErrors = remainingErrors;
      }
    }
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    // Close dropdown when clicking outside trigger/panel
    if (!this.groupsDropdownOpen) return;

    const target = event.target as HTMLElement | null;
    if (!target) return;

    const triggerEl = target.closest('[data-groups-trigger]');
    const panelEl = target.closest('[data-groups-panel]');

    if (!triggerEl && !panelEl) {
      this.groupsDropdownOpen = false;
    }
  }

  hasFieldError(fieldName: string, errorName: string): boolean {
    const error = this.formErrors[fieldName];
    if (!error) {
      return false;
    }

    return typeof error === 'object' && error[errorName] === true;
  }

  private validateTitle(title: string, rawTitle: string): FieldValidationErrors {
    const errors: FieldValidationErrors = {};

    if (!rawTitle) {
      errors['required'] = true;
    }
    if (rawTitle.length > 0 && !title) {
      errors['noSpaces'] = true;
    }
    if (title && rawTitle !== title) {
      errors['trim'] = true;
    }
    if (title && title.length < 2) {
      errors['minlength'] = true;
    }
    if (title.length > 100) {
      errors['maxlength'] = true;
    }
    if (title && !this.titlePattern.test(title)) {
      errors['pattern'] = true;
    }

    return errors;
  }

  private validateDescription(description: string, rawDescription: string): FieldValidationErrors {
    const errors: FieldValidationErrors = {};

    if (!rawDescription) {
      errors['required'] = true;
    }
    if (rawDescription.length > 0 && !description) {
      errors['noSpaces'] = true;
    }
    if (description && rawDescription !== description) {
      errors['trim'] = true;
    }
    if (description && description.length < 10) {
      errors['minlength'] = true;
    }
    if (description.length > 1000) {
      errors['maxlength'] = true;
    }
    if (description && !this.descriptionPattern.test(description)) {
      errors['pattern'] = true;
    }

    return errors;
  }

  onValidatedFieldChange(fieldName: 'title' | 'description' | 'venue' | 'city' | 'state'): void {
    if (!this.isSubmitted) {
      return;
    }

    const rawValue = this[fieldName] ?? '';
    const trimmedValue = rawValue.trim();
    const fieldErrors = this.getValidatedFieldErrors(fieldName, trimmedValue, rawValue);

    if (Object.keys(fieldErrors).length > 0) {
      this.formErrors = {
        ...this.formErrors,
        [fieldName]: fieldErrors,
      };
      return;
    }

    const { [fieldName]: _removedError, ...remainingErrors } = this.formErrors;
    this.formErrors = remainingErrors;
  }

  private validateVenue(venue: string, rawVenue: string): FieldValidationErrors {
    const errors: FieldValidationErrors = {};

    if (!rawVenue) {
      errors['required'] = true;
    }
    if (rawVenue.length > 0 && !venue) {
      errors['noSpaces'] = true;
    }
    if (venue && rawVenue !== venue) {
      errors['trim'] = true;
    }
    if (venue && venue.length < 5) {
      errors['minlength'] = true;
    }
    if (venue.length > 255) {
      errors['maxlength'] = true;
    }
    if (venue && !this.venuePattern.test(venue)) {
      errors['pattern'] = true;
    }

    return errors;
  }

  private validateCityState(value: string, rawValue: string): FieldValidationErrors {
    const errors: FieldValidationErrors = {};

    if (!rawValue) {
      errors['required'] = true;
    }
    if (rawValue.length > 0 && !value) {
      errors['noSpaces'] = true;
    }
    if (value && rawValue !== value) {
      errors['trim'] = true;
    }
    if (value && value.length < 2) {
      errors['minlength'] = true;
    }
    if (value.length > 50) {
      errors['maxlength'] = true;
    }
    if (value && !this.cityStatePattern.test(value)) {
      errors['pattern'] = true;
    }

    return errors;
  }

  private getValidatedFieldErrors(
    fieldName: 'title' | 'description' | 'venue' | 'city' | 'state',
    trimmedValue: string,
    rawValue: string,
  ): FieldValidationErrors {
    if (fieldName === 'title') {
      return this.validateTitle(trimmedValue, rawValue);
    }
    if (fieldName === 'description') {
      return this.validateDescription(trimmedValue, rawValue);
    }
    if (fieldName === 'venue') {
      return this.validateVenue(trimmedValue, rawValue);
    }

    return this.validateCityState(trimmedValue, rawValue);
  }

  private parseDateInput(value: string): Date | null {
    if (!value) {
      return null;
    }

    const [year, month, day] = value.split('-').map((part) => Number(part));
    if (![year, month, day].every((part) => Number.isFinite(part))) {
      return null;
    }

    return new Date(year, month - 1, day);
  }

  private parseDateTimeInput(dateValue: string, timeValue: string): Date | null {
    const date = this.parseDateInput(dateValue);
    if (!date || !timeValue) {
      return null;
    }

    const [hours, minutes] = timeValue.split(':').map((part) => Number(part));
    if (![hours, minutes].every((part) => Number.isFinite(part))) {
      return null;
    }

    date.setHours(hours, minutes, 0, 0);
    return date;
  }

  private validateDateFields(errors: Record<string, FormErrorValue>): void {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const start = this.parseDateInput(this.start_date);
    const end = this.parseDateInput(this.end_date);

    if (!this.start_date) {
      errors['start_date'] = 'Start date is required.';
    } else if (start && start.getTime() < today.getTime()) {
      errors['start_date'] = 'Start date cannot be in the past.';
    }

    if (!this.end_date) {
      errors['end_date'] = 'End date is required.';
    } else if (start && end && end.getTime() < start.getTime()) {
      errors['end_date'] = 'End date must be greater than or equal to start date.';
    }
  }

  private validateTimeFields(errors: Record<string, FormErrorValue>): void {
    const startDateTime = this.parseDateTimeInput(this.start_date, this.start_time);
    const endDateTime = this.parseDateTimeInput(this.end_date, this.end_time);

    if (!(this.start_time ?? '').trim()) {
      errors['start_time'] = 'Start time is required.';
    } else if (!errors['start_date'] && startDateTime && startDateTime.getTime() < Date.now()) {
      errors['start_time'] = 'Start time cannot be in the past.';
    }

    if (!(this.end_time ?? '').trim()) {
      errors['end_time'] = 'End time is required.';
    } else if (!errors['start_date'] && !errors['end_date'] && startDateTime && endDateTime && endDateTime.getTime() <= startDateTime.getTime()) {
      errors['end_time'] = 'End time must be greater than start time.';
    }
  }

  private validateNoOfDays(errors: Record<string, FormErrorValue>): void {
    if (this.no_of_days === null || this.no_of_days === undefined || !Number.isFinite(this.no_of_days)) {
      errors['no_of_days'] = 'No. of days is required.';
    } else if (Number(this.no_of_days) < 1) {
      errors['no_of_days'] = 'No. of days must be at least 1.';
    }
  }

  private onValidatedScheduleChange(): void {
    if (!this.isSubmitted) {
      return;
    }

    const scheduleErrors: Record<string, FormErrorValue> = {};
    this.validateNoOfDays(scheduleErrors);
    this.validateDateFields(scheduleErrors);
    this.validateTimeFields(scheduleErrors);

    const nextErrors = { ...this.formErrors };
    delete nextErrors['no_of_days'];
    delete nextErrors['start_date'];
    delete nextErrors['end_date'];
    delete nextErrors['start_time'];
    delete nextErrors['end_time'];

    ['no_of_days', 'start_date', 'end_date', 'start_time', 'end_time'].forEach((fieldName) => {
      if (scheduleErrors[fieldName]) {
        nextErrors[fieldName] = scheduleErrors[fieldName];
      }
    });

    this.formErrors = nextErrors;
  }

  private validatePricePerHead(): void {
    const nextErrors = { ...this.formErrors };
    delete nextErrors['price_per_head'];

    if (this.ticket_type === 'Paid') {
      const price = this.price_per_head;
      if (price === null || price === undefined || price === ('' as any) || !Number.isFinite(Number(price))) {
        nextErrors['price_per_head'] = 'Price per head is required for Paid ticket.';
      } else if (Number(price) <= 0) {
        nextErrors['price_per_head'] = 'Price per head must be greater than 0.';
      } else if (String(Math.trunc(Number(price))).length > 7) {
        nextErrors['price_per_head'] = 'Price per head must not exceed 7 digits.';
      }
    }

    this.formErrors = nextErrors;
  }

  private validateForm(): boolean {
    const errors: Record<string, FormErrorValue> = {};

    const rawTitle = this.title ?? '';
    const rawDescription = this.description ?? '';
    const rawVenue = this.venue ?? '';
    const rawCity = this.city ?? '';
    const rawState = this.state ?? '';
    const titleTrim = rawTitle.trim();
    const descriptionTrim = rawDescription.trim();
    const venueTrim = rawVenue.trim();
    const cityTrim = rawCity.trim();
    const stateTrim = rawState.trim();

    const titleErrors = this.validateTitle(titleTrim, rawTitle);
    if (Object.keys(titleErrors).length > 0) {
      errors['title'] = titleErrors;
    }

    const descriptionErrors = this.validateDescription(descriptionTrim, rawDescription);
    if (Object.keys(descriptionErrors).length > 0) {
      errors['description'] = descriptionErrors;
    }

    const venueErrors = this.validateVenue(venueTrim, rawVenue);
    if (Object.keys(venueErrors).length > 0) {
      errors['venue'] = venueErrors;
    }

    const cityErrors = this.validateCityState(cityTrim, rawCity);
    if (Object.keys(cityErrors).length > 0) {
      errors['city'] = cityErrors;
    }

    const stateErrors = this.validateCityState(stateTrim, rawState);
    if (Object.keys(stateErrors).length > 0) {
      errors['state'] = stateErrors;
    }

    this.validateNoOfDays(errors);
    this.validateDateFields(errors);
    this.validateTimeFields(errors);

    if (this.ticket_type === 'Paid') {
      const price = this.price_per_head;
      if (price === null || price === undefined || price === ('' as any) || !Number.isFinite(Number(price))) {
        errors['price_per_head'] = 'Price per head is required for Paid ticket.';
      } else if (Number(price) <= 0) {
        errors['price_per_head'] = 'Price per head must be greater than 0.';
      } else if (String(Math.trunc(Number(price))).length > 7) {
        errors['price_per_head'] = 'Price per head must not exceed 7 digits.';
      }
    }

    if (this.participant_type === 'Group') {
      if (!Array.isArray(this.selected_groups) || (this.selected_groups ?? []).length === 0) {
        errors['selected_groups'] = 'Please select at least one group.';
      }
    }

    if (!(this.registerion_link ?? '').trim()) {
      errors['registerion_link'] = 'Registration link is required.';
    } else {
      const link = this.registerion_link.trim();

      // Must be a valid URL. If user enters something without scheme, allow only simple form
      // by trying to validate as https://<value>.
      // If scheme is missing, treat it as invalid (require full URL like https://example.com)
      const hasScheme = /^https?:\/\//i.test(link);
      if (!hasScheme) {
        errors['registerion_link'] = 'Enter a valid url';
      } else {
        try {
          const url = new URL(link);
          if (!/^https?:$/.test(url.protocol) || !url.hostname) {
            errors['registerion_link'] = 'Enter a valid url';
          }
        } catch {
          errors['registerion_link'] = 'Enter a valid url';
        }
      }
    }

    if (!(this.registration_deadline_date ?? '').trim()) {
      errors['registration_deadline_date'] = 'Registration deadline date is required.';
    } else {
      // registration_deadline_date must be strictly less than start_date
      const deadline = this.parseDateInput(this.registration_deadline_date);
      const start = this.parseDateInput(this.start_date);

      if (!deadline) {
        errors['registration_deadline_date'] = 'Registration deadline date is invalid.';
      } else if (start && deadline.getTime() > start.getTime()) {
        errors['registration_deadline_date'] = 'Registration deadline must be less than or equal to start date.';
      }
    }


    this.formErrors = errors;
    const isValid = Object.keys(errors).length === 0;
    if (isValid) {
      this.title = titleTrim;
      this.description = descriptionTrim;
      this.venue = venueTrim;
      this.city = cityTrim;
      this.state = stateTrim;
    }

    return isValid;
  }

  onPublishConclave() {
    // prevent multiple record creation on fast repeated clicks
    if (this.isPublishLocked || this.isSubmitting) {
      return;
    }

    this.errorMessage = '';
    this.isSubmitted = true;

    if (!this.validateForm()) {
      this.isSubmitting = false;
      this.isPublishLocked = false;
      return;
    }

    this.isPublishLocked = true;
    this.isSubmitting = true;

    const formData = new FormData();



    // backend validation requires user_id
    const userId = 68;
    formData.append('user_id', String(userId));

    formData.append('title', this.title);

    formData.append('description', this.description);
    formData.append('venue', this.venue);
    formData.append('city', this.city);
    formData.append('state', this.state);
    formData.append('no_of_days', String(this.no_of_days ?? ''));

    formData.append('start_date', this.start_date);
    formData.append('start_time', this.start_time);
    formData.append('end_date', this.end_date);
    formData.append('end_time', this.end_time);

    formData.append('ticket_type', this.ticket_type);
    formData.append('price_per_head', String(this.price_per_head ?? ''));
    formData.append('participant_type', this.participant_type);

    formData.append('registerion_link', this.registerion_link);
    formData.append('registration_deadline_date', this.registration_deadline_date);
    // backend validator expects boolean
    formData.append('is_active', this.is_active ? '1' : '0');



    // - selected_groups must be an array
    // FormData doesn't support real arrays; backend often expects repeated keys like selected_groups[]
    (this.selected_groups ?? []).forEach((groupId) => {
      formData.append('selected_groups[]', String(groupId));
    });

    // - cover_image_url should be the binary file (multipart upload)
    // If no file selected, send empty string to keep backend validator happy.
    if (this.selectedCoverFile) {
      formData.append('cover_image_url', this.selectedCoverFile, this.selectedCoverFile.name);
    } else {
      formData.append('cover_image_url', this.cover_image_url ?? '');
    }




    this.conclaveApi.createConclave(formData).subscribe({
      next: () => {
        this.isSubmitting = false;
        this.isPublishLocked = false;
        this.router.navigate(['/conclave']);
      },
      error: (err:any) => {
        this.isSubmitting = false;
        this.isPublishLocked = false;
        this.errorMessage = err?.error?.message || 'Failed to create conclave';
        console.error(err);
      }
    });
  }
}

